// Pure helpers for People → Staff attendance, Schedules → Settings and the
// attendance report (spec docs/superpowers/specs/2026-09-16-staff-schedules-attendance-design.md §5).
// Rules are computed server-side (server/src/staff/attendance-rules.ts); this
// file only shapes, filters and labels. Every date and time is shown in the
// ORG timezone, never the browser's (MyCRM mixed the two — spec §9.9).

export const DEFAULT_TIMEZONE = 'Asia/Baghdad';
export const TIMEZONES = [
  'UTC', 'Asia/Baghdad', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Tehran',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Istanbul',
  'Europe/Moscow', 'Africa/Cairo', 'Asia/Kolkata', 'Asia/Shanghai',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles',
];
// Mirror of server/src/lib/staff-roles.ts.
export const STAFF_ROLES = ['driver', 'field_worker', 'inspector', 'patrol', 'supervisor', 'back_office', 'other'];

const pad2 = (n) => String(n).padStart(2, '0');
const DAY_MS = 86400000;

function isValidTimeZone(tz) {
  if (typeof tz !== 'string' || !tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function orgTimezone(settings) {
  const tz = settings?.attendance?.timezone;
  return isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
}

function wallClock(date, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(date);
  const p = {};
  for (const part of parts) p[part.type] = part.value;
  return { y: p.year, mo: p.month, d: p.day, h: p.hour, mi: p.minute };
}

export function todayInTz(tz, now = new Date()) {
  const w = wallClock(now, tz);
  return `${w.y}-${w.mo}-${w.d}`;
}

export function addDays(date, n) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function dayCount(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

export function presetRange(preset, tz, now = new Date()) {
  const today = todayInTz(tz, now);
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week') return { from: addDays(today, -new Date(`${today}T00:00:00Z`).getUTCDay()), to: today };
  if (preset === 'month') return { from: `${today.slice(0, 8)}01`, to: today };
  throw new Error(`Unknown preset ${preset}`);
}

export function localDateTimeInTz(iso, tz) {
  if (!iso) return '';
  const w = wallClock(new Date(iso), tz);
  return `${w.y}-${w.mo}-${w.d}T${w.h}:${w.mi}`;
}

export function timeInTz(iso, tz) {
  return iso ? localDateTimeInTz(iso, tz).slice(11) : '—';
}

/**
 * "YYYY-MM-DD HH:MM" in the org timezone — the readable instant for report
 * cells and CSV columns. Reports used to call Date#toLocaleString(), which
 * renders the BROWSER's timezone: the same clock-in then read differently on
 * the Attendance tab (org time) and in a report (local time).
 */
export function dateTimeInTz(iso, tz) {
  const local = localDateTimeInTz(iso, tz);
  return local ? local.replace('T', ' ') : '—';
}

/**
 * The "YYYY-MM-DD" calendar day an instant falls on in the org timezone.
 * Comparing these strings lexicographically is how a day-range filter stays
 * correct without any boundary arithmetic — and keeps the filter agreeing with
 * the date the row displays.
 */
export function dayInTz(iso, tz) {
  return localDateTimeInTz(iso, tz).slice(0, 10);
}

export function outTimeLabel(row, tz) {
  if (!row.clocked_out_at) return '—';
  const local = localDateTimeInTz(row.clocked_out_at, tz);
  const time = local.slice(11);
  return row.work_date && local.slice(0, 10) > row.work_date ? `${time} (+1)` : time;
}

// C2 (controller ruling, 2026-09-17): a night-shift clock-in is carried to the
// PREVIOUS work_date server-side (attendance-rules.ts evaluateShift), so the
// clock-in's own local date can be later than work_date. Mark that with the
// same "(+1)" convention as outTimeLabel so the In column doesn't look wrong.
export function inTimeLabel(row, tz) {
  const local = localDateTimeInTz(row.clocked_in_at, tz);
  const time = local.slice(11);
  return row.work_date && local.slice(0, 10) > row.work_date ? `${time} (+1)` : time;
}

export function formatMinutes(min) {
  if (min == null) return '—';
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}h ${pad2(m % 60)}m`;
}

export function minutesToHours(min) {
  return Math.round(((min || 0) / 60) * 10) / 10;
}

export function formatDistance(m) {
  if (m == null) return '';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

// Literal t() calls on purpose — scripts/check-i18n.js cannot see computed keys.
export function locationLabel(row, t) {
  if (row.geofence_status === 'inside') {
    const office = row.geofence_poi_name || '—';
    if (row.expected_poi_id && row.geofence_poi_id && row.expected_poi_id !== row.geofence_poi_id && row.expected_poi_name) {
      return t('attendance.location.expected', '{{office}} (expected {{expected}})', { office, expected: row.expected_poi_name });
    }
    return office;
  }
  if (row.geofence_status === 'outside') {
    return t('attendance.location.outside', 'Outside — {{distance}} from {{office}}', {
      distance: formatDistance(row.geofence_distance_m), office: row.geofence_poi_name || '—',
    });
  }
  if (row.geofence_status === 'exempt') return t('attendance.location.exempt', 'Exempt');
  return t('attendance.location.notChecked', 'Not checked');
}

export function statusLabel(row, t) {
  if (!row.clocked_out_at) return t('attendance.status.open', 'Clocked in');
  if (row.auto_closed) return t('attendance.status.autoClosed', 'Auto-closed');
  return t('attendance.status.closed', 'Clocked out');
}

export function sourceLabel(source, t) {
  if (source === 'admin') return t('attendance.source.admin', 'Manual');
  if (source === 'web') return t('attendance.source.web', 'Web');
  return t('attendance.source.mobile', 'Staff app');
}

export function lateLabel(row, t) {
  if (row.is_late == null) return '—';
  return row.is_late
    ? t('attendance.lateBadge', 'Late {{minutes}}m', { minutes: row.late_minutes })
    : t('attendance.onTime', 'On time');
}

export function roleLabel(role, t) {
  switch (role) {
    case 'driver': return t('attendance.roles.driver', 'Driver');
    case 'field_worker': return t('attendance.roles.field_worker', 'Field worker');
    case 'inspector': return t('attendance.roles.inspector', 'Inspector');
    case 'patrol': return t('attendance.roles.patrol', 'Patrol');
    case 'supervisor': return t('attendance.roles.supervisor', 'Supervisor');
    case 'back_office': return t('attendance.roles.back_office', 'Back office');
    default: return t('attendance.roles.other', 'Other');
  }
}

const CARD_STATUS = { entries: 'all', clockedIn: 'open', late: 'late', outside: 'outside', absent: 'absent' };

// C3 (controller ruling, 2026-09-17): "Clocked in now" counts open rows with
// work_date >= today − 1 (night staff stay counted after midnight), so its
// card filter looks back one extra day; every other card stays today-only.
export function cardFilter(card, tz, now = new Date()) {
  const status = CARD_STATUS[card];
  if (card === 'entries') return { status };
  const today = todayInTz(tz, now);
  if (card === 'clockedIn') return { status, from: addDays(today, -1), to: today };
  return { status, from: today, to: today };
}

const SERVER_STATUSES = ['open', 'late', 'outside', 'auto_closed'];

export function listParams({ status, userId, from, to }) {
  return { status: SERVER_STATUSES.includes(status) ? status : undefined, userId: userId || undefined, from, to };
}

// Default clock-in: today 09:00 in the org timezone, or now (to the minute) when
// it isn't 09:00 yet — a future default would be rejected by the server.
export function emptyEntryForm(tz, now = new Date()) {
  const nineAm = `${todayInTz(tz, now)}T09:00`;
  const current = localDateTimeInTz(now, tz);
  return { userId: '', clockIn: current < nineAm ? current : nineAm, clockOut: '', breakMinutes: 0, notes: '' };
}

export function entryToForm(row, tz) {
  return {
    userId: row.user_id,
    clockIn: localDateTimeInTz(row.clocked_in_at, tz),
    clockOut: localDateTimeInTz(row.clocked_out_at, tz),
    breakMinutes: row.break_minutes ?? 0,
    notes: row.notes ?? '',
    // Phase 3: recorded breaks, editable as wall-clock pairs in the org timezone.
    // An open break (no end yet) is shown with an empty end and must be ended to save.
    breaks: (row.breaks || []).map((b) => ({ start: localDateTimeInTz(b.started_at, tz), end: localDateTimeInTz(b.ended_at, tz) })),
  };
}

function payloadFields(form) {
  return {
    clockIn: form.clockIn,
    clockOut: form.clockOut || null,
    breakMinutes: Number.parseInt(form.breakMinutes, 10) || 0,
    notes: String(form.notes || '').trim() || null,
  };
}

// An edit sends ONLY the fields that changed from the form it opened with
// (`initialForm`): the form works in whole minutes, so re-sending an untouched
// time would overwrite a stored 13:04:37 with 13:04 and clear "Estimated".
export function formToEntryPayload(form, isNew, initialForm) {
  const body = payloadFields(form);
  if (isNew) return { userId: form.userId, ...body };
  const breaks = (form.breaks || []).map((b) => ({ start: b.start, end: b.end }));
  if (!initialForm) return breaks.length ? { ...body, breaks } : body;
  const before = payloadFields(initialForm);
  const changed = Object.fromEntries(Object.entries(body).filter(([k, v]) => v !== before[k]));
  const beforeBreaks = (initialForm.breaks || []).map((b) => ({ start: b.start, end: b.end }));
  if (JSON.stringify(breaks) !== JSON.stringify(beforeBreaks)) {
    // The server derives break minutes from the list; a manual total would be ignored.
    delete changed.breakMinutes;
    changed.breaks = breaks;
  }
  return changed;
}

export function attendanceErrorMessage(err, t) {
  const code = err?.message;
  if (code === 'overlap') return t('attendance.errors.overlap', 'This overlaps another entry for the same person.');
  if (code === 'already_open') return t('attendance.errors.alreadyOpen', 'This person already has an open entry.');
  if (code === 'clock_out_before_in') return t('attendance.errors.outBeforeIn', 'Clock out must be after clock in.');
  if (code === 'entry_too_long') return t('attendance.errors.tooLong', "An entry can't be longer than 24 hours.");
  if (code === 'clock_in_in_future' || code === 'clock_out_in_future') return t('attendance.errors.future', "Times can't be in the future.");
  if (code === 'break_exceeds_duration') return t('attendance.errors.break', "Break can't be longer than the time worked.");
  if (code === 'cannot_reopen') return t('attendance.errors.cannotReopen', "A closed entry can't be reopened.");
  if (code === 'user_not_found') return t('attendance.errors.userNotFound', 'That person is not a staff member of this organization.');
  // C5 (controller ruling, 2026-09-17): the work-office picker (Task 8) can post an
  // out-of-category POI; the server's raw 'invalid_work_poi' must not reach the user.
  if (code === 'invalid_work_poi') return t('attendance.errors.invalidWorkPoi', 'Choose an office from the office category.');
  if (code === 'invalid_breaks') return t('attendance.errors.invalidBreaks', 'Each break needs a start and an end inside the entry, and breaks cannot overlap.');
  return t('attendance.errors.generic', 'Could not save. Please try again.');
}

export function attendanceCsv(rows, tz, t) {
  const headers = [
    t('attendance.col.person', 'Person'), t('attendance.col.date', 'Date'), t('attendance.col.in', 'In'),
    t('attendance.col.out', 'Out'), t('attendance.col.worked', 'Worked'), t('attendance.col.break', 'Break'),
    t('attendance.col.late', 'Late'), t('attendance.col.location', 'Location'), t('attendance.col.status', 'Status'),
    t('attendance.col.source', 'Source'),
  ];
  return {
    headers,
    rows: rows.map((r) => [
      r.user_name || '',
      r.work_date || '',
      inTimeLabel(r, tz),
      r.clocked_out_at ? `${outTimeLabel(r, tz)}${r.auto_closed ? ` (${t('attendance.estimated', 'Estimated')})` : ''}` : '',
      formatMinutes(r.worked_minutes),
      formatMinutes(r.break_minutes),
      lateLabel(r, t),
      locationLabel(r, t),
      statusLabel(r, t),
      sourceLabel(r.source, t),
    ]),
  };
}

// ── Phase 3: photos, breaks, synced later (combined app release spec §8.4) ──

/** 'photo' | 'expired' | 'old_app' | 'none' for the In (clock-in) or Out (clock-out) photo. */
export function photoState(row, kind) {
  if (kind === 'out') return row.has_clock_out_photo ? 'photo' : 'none';
  if (row.has_clock_in_photo) return 'photo';
  if (row.clock_in_photo_status === 'expired') return 'expired';
  if (row.clock_in_photo_status === 'old_app') return 'old_app';
  return 'none';
}

/** Recorded breaks as org-timezone times; an open break ends with '—'. */
export function breakRows(row, tz) {
  return (row.breaks || []).map((b) => ({ start: timeInTz(b.started_at, tz), end: b.ended_at ? timeInTz(b.ended_at, tz) : '—' }));
}

/** A new empty break row for the edit form, defaulting to the entry's clock-in minute. */
export function newBreakRow(form) {
  return { start: form.clockIn || '', end: '' };
}

const intOr = (v, fallback) => (Number.isInteger(v) ? v : fallback);

export function settingsToForm(raw) {
  const a = raw && typeof raw === 'object' ? raw : {};
  return {
    timezone: isValidTimeZone(a.timezone) ? a.timezone : DEFAULT_TIMEZONE,
    officePoiCategoryId: a.officePoiCategoryId || '',
    defaultWorkPoiId: a.defaultWorkPoiId || '',
    geofenceEnabled: a.geofenceEnabled === true,
    exemptRoles: Array.isArray(a.exemptRoles) ? a.exemptRoles.filter((r) => STAFF_ROLES.includes(r)) : [],
    requireClockInPhoto: a.requireClockInPhoto !== false,
    autoCloseHours: intOr(a.autoCloseHours, 16),
    defaultWorkdayHours: intOr(a.defaultWorkdayHours, 8),
    absentAfterMinutes: intOr(a.absentAfterMinutes, 60),
  };
}

function clampInt(v, min, max, fallback) {
  const n = Number.parseInt(v, 10);
  return Number.isInteger(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

// Same bounds as readAttendanceSettings on the server.
export function formToSettings(form) {
  return {
    timezone: form.timezone,
    officePoiCategoryId: form.officePoiCategoryId || null,
    defaultWorkPoiId: form.officePoiCategoryId && form.defaultWorkPoiId ? form.defaultWorkPoiId : null,
    geofenceEnabled: !!form.geofenceEnabled,
    exemptRoles: [...form.exemptRoles],
    requireClockInPhoto: !!form.requireClockInPhoto,
    autoCloseHours: clampInt(form.autoCloseHours, 1, 48, 16),
    defaultWorkdayHours: clampInt(form.defaultWorkdayHours, 1, 24, 8),
    absentAfterMinutes: clampInt(form.absentAfterMinutes, 0, 720, 60),
  };
}

export function hoursByPersonChart(people, limit = 15) {
  return people.slice(0, limit).map((p) => ({ name: p.name, hours: minutesToHours(p.totalMinutes) }));
}

export function presenceChart(days) {
  return days.map((d) => ({ label: d.date.slice(5), present: d.present }));
}

export function reportCsv(people, t) {
  return {
    headers: [
      t('attendance.col.person', 'Person'), t('attendance.report.daysPresent', 'Days present'),
      t('attendance.report.totalHours', 'Total hours'), t('attendance.report.avgPerDay', 'Avg hours/day'),
      t('attendance.report.lateCount', 'Late entries'),
    ],
    rows: people.map((p) => [p.name, p.daysPresent, minutesToHours(p.totalMinutes), minutesToHours(p.avgMinutesPerDay), p.lateCount]),
  };
}
